<template>
  <div>
    <!-- PDF Preview Component -->
    <pdf-preview
      v-show="inPreview"
      :inPreview="inPreview"
      :generatingPreview="generatingPreview"
      :saving="saving"
      :pdfUrl="pdfUrl"
      :backToForm="backToForm"
      :save="save"
    />
    
    <!-- Formulario -->
    <form v-show="!inPreview" data-vv-scope="custom-document" autocomplete="off" @submit.prevent>
      <h2 class="mb-10">Decreto Alcaldicio</h2>
      
      <!-- Sección Identificación -->
      <h4 id="seccion-identificacion">Identificación</h4>
      <div class="row" role="group" aria-labelledby="seccion-identificacion">
        <div class="col-md-6 col-12">
          <b-form-group label="Empresa" label-for="empresa">
            <b-form-input
              id="empresa"
              v-model="empresa.razonSocial"
              name="empresa"
              aria-label="Empresa, campo de solo lectura"
              readonly
              disabled
            />
          </b-form-group>
        </div>
        
        <div class="col-md-6 col-12">
          <b-form-group label="Comuna" label-for="comuna">
            <b-form-input
              id="comuna"
              v-model="formData.comuna"
              name="comuna"
              aria-label="Comuna Machalí, campo de solo lectura"
              disabled
            />
          </b-form-group>
        </div>
      </div>
      
      <vs-divider />
      
      <!-- Sección Cuerpo del Decreto -->
      <h4 id="seccion-cuerpo">Cuerpo del Decreto</h4>
      <div class="row" role="group" aria-labelledby="seccion-cuerpo">
        <div class="col-12">
          <h5 class="mb-2">Vistos *</h5>
          <EditorText
            v-model="formData.vistos"
            name="vistos"
            data-vv-as="vistos"
            v-validate="'required'"
            :danger="errors.has('custom-document.vistos')"
            :danger-text="errors.first('custom-document.vistos')"
            :max-length="100000"
            :toolbar-items="{
              items: [
                'undo', 'redo',
                '|', 'heading',
                '|', 'bold', 'italic',
                '|', 'insertTable',
                '|', 'numberedList', 'bulletedList'
              ]
            }"
            with-counter
            aria-label="Campo de texto enriquecido para Vistos, campo requerido"
            aria-required="true"
            :aria-invalid="errors.has('custom-document.vistos') ? 'true' : 'false'"
          />
        </div>
        
        <div class="col-12 mt-3">
          <h5 class="mb-2">Considerando *</h5>
          <EditorText
            v-model="formData.considerando"
            name="considerando"
            data-vv-as="considerando"
            v-validate="'required'"
            :danger="errors.has('custom-document.considerando')"
            :danger-text="errors.first('custom-document.considerando')"
            :max-length="100000"
            :toolbar-items="{
              items: [
                'undo', 'redo',
                '|', 'heading',
                '|', 'bold', 'italic',
                '|', 'insertTable',
                '|', 'numberedList', 'bulletedList'
              ]
            }"
            with-counter
            aria-label="Campo de texto enriquecido para Considerando, campo requerido"
            aria-required="true"
            :aria-invalid="errors.has('custom-document.considerando') ? 'true' : 'false'"
          />
        </div>
        
        <div class="col-12 mt-3">
          <h5 class="mb-2">Decreto *</h5>
          <EditorText
            v-model="formData.decreto"
            name="decreto"
            data-vv-as="decreto"
            v-validate="'required'"
            :danger="errors.has('custom-document.decreto')"
            :danger-text="errors.first('custom-document.decreto')"
            :max-length="100000"
            :toolbar-items="{
              items: [
                'undo', 'redo',
                '|', 'heading',
                '|', 'bold', 'italic',
                '|', 'insertTable',
                '|', 'numberedList', 'bulletedList'
              ]
            }"
            with-counter
            aria-label="Campo de texto enriquecido para Decreto, campo requerido"
            aria-required="true"
            :aria-invalid="errors.has('custom-document.decreto') ? 'true' : 'false'"
          />
        </div>
      </div>
      
      <vs-divider />
      
      <!-- Sección Visadores -->
      <h4 id="seccion-visadores">Visadores</h4>
      <div class="row" role="group" aria-labelledby="seccion-visadores">
        <div class="col-12">
          <ListUser
            v-model="formData.visadores"
            label="Visadores del proceso *"
            placeholder="Selecciona el/los visadores"
            name="visadores"
            data-vv-as="visadores"
            :is-multiple="true"
            :autocomplete="true"
            :danger="visatorsError !== ''"
            :danger-text="visatorsError"
            aria-label="Selección múltiple de visadores con autocompletado, campo requerido, mínimo 1 visador"
            aria-required="true"
            :aria-invalid="visatorsError !== '' ? 'true' : 'false'"
            @full-user="handleVisadoresChange"
          />
        </div>
      </div>
      
      <vs-divider />
      
      <!-- Sección Distribución -->
      <h4 id="seccion-distribucion">Distribución</h4>
      <div class="row" role="group" aria-labelledby="seccion-distribucion">
        <!-- Distribución Interna -->
        <div class="col-12">
          <h5 class="mb-3 mt-2">Distribución Interna</h5>
        </div>
        
        <div class="col-md-6 col-12">
          <b-form-group label="Direcciones" label-for="directions">
            <v-select
              id="directions"
              v-model="formData.directions"
              name="directions"
              data-cy="directions"
              :options="directions"
              multiple
              label="label"
              placeholder="Selecciona la/s direccion/es"
              :loading="loadingDirecciones"
              aria-label="Selección múltiple de direcciones, campo opcional"
              :aria-busy="loadingDirecciones ? 'true' : 'false'"
              @input="searchUnits"
            >
              <span slot="no-options">no hay direcciones</span>
            </v-select>
          </b-form-group>
          <div class="mt-2">
            <b-form-checkbox
              v-model="selectAllDirections"
              :disabled="!directions.length"
              class="d-inline-block"
              data-cy="select-all-directions"
              @change="selectDirections(selectAllDirections)"
            >
              Seleccionar todas
            </b-form-checkbox>
          </div>
        </div>
        
        <div v-if="formData.directions.length" class="col-md-6 col-12">
          <b-form-group label="Unidad" label-for="units">
            <v-select
              id="units"
              v-model="formData.units"
              name="units"
              data-cy="units"
              :options="units"
              multiple
              label="label"
              placeholder="Selecciona la/s unidad/es"
              :loading="loadingDepartamentos"
              :aria-label="'Selección múltiple de unidades, campo opcional'"
              :aria-busy="loadingDepartamentos ? 'true' : 'false'"
            >
              <span slot="no-options">no hay unidades</span>
            </v-select>
          </b-form-group>
          <div class="mt-2">
            <b-form-checkbox
              v-model="selectAllUnits"
              :disabled="!units.length"
              class="d-inline-block"
              data-cy="select-all-units"
              @change="selectUnits(selectAllUnits)"
            >
              Seleccionar todas
            </b-form-checkbox>
          </div>
        </div>
        
        <!-- Separador visual -->
        <div class="col-12 mt-4 mb-3">
          <vs-divider />
        </div>
        
        <!-- Distribución Externa -->
        <div class="col-12">
          <h5 class="mb-3">Distribución Externa</h5>
        </div>
        
        <div class="col-12">
          <EditorText
            ref="externalDistribution"
            v-model="formData.distribucionExterna"
            name="externalDistribution"
            data-cy="externalDistribution"
            data-vv-as="distribución externa"
            :max-length="100000"
            :toolbar-items="{
              items: [
                'undo', 'redo',
                '|', 'heading',
                '|', 'bold', 'italic',
                '|', 'insertTable',
                '|', 'numberedList', 'bulletedList'
              ]
            }"
            with-counter
            aria-label="Campo de texto enriquecido para distribución externa, campo opcional"
          />
        </div>
      </div>
      
      <vs-divider />
      
      <!-- Sección Adjuntos -->
      <h4 id="seccion-adjuntos">Adjuntos (Opcional)</h4>
      <div class="row" role="group" aria-labelledby="seccion-adjuntos">
        <div class="col-12">
          <div class="row align-items-center">
            <div class="col-md-3 align-items-end">
              <UploadFile
                permission="*"
                accept="application/pdf, .doc, .docx, .xlsx, .ppt"
                text="Seleccionar documentos"
                v-model="fileOthers"
                upload-to="ed"
                :button-class="{ 'btn-block': true }"
                container-class="w-100"
                @input="addOthersFile()"
              />
            </div>
          </div>
          <div class="row align-items-center mt-1">
            <div class="col-md-3 align-items-end">
              <b-button
                variant="secondary"
                class="btn-icon mt-1"
                data-cy="from-repository"
                v-has-permission="'ED060'"
                v-b-modal="'add-file-repository'"
              >
                <feather-icon icon="ServerIcon"/>
                Desde el repositorio
              </b-button>
            </div>
            <div class="col-2" v-for="file in formData.attachments" :key="file.id">
              <div class="d-flex flex-column align-items-center">
                <vs-icon icon="description" size="25px" color="primary" />
                <b class="text-truncate w-100 text-center">{{ file.name }}</b>
                <vs-button
                  color="danger"
                  type="flat"
                  icon="clear"
                  @click="removeOthersFile(file.id)"
                />
              </div>
            </div>
          </div>
          <div
            v-if="!!error.messageOthers"
            class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to text-left"
          >
            <span class="span-text-validation">
              {{ error.messageOthers }}
            </span>
          </div>
        </div>
      </div>
    
    <!-- RepositorySelector Modal -->
    <repository-selector
      modal-id="add-file-repository"
      :add-file="addFileRepo"
      :files="formData.attachments"
      :remove-file="removeOthersFile"
    />
    
    <!-- Acciones -->
    <div class="row justify-end" role="group" aria-label="Acciones del formulario">
      <vs-button
        class="mt-5 mr-5"
        data-cy="cancel"
        @click="back()"
        color="primary"
        :disabled="saving"
      >
        Cancelar
      </vs-button>
      <vs-button
        class="mt-5 mr-5"
        data-cy="preview"
        @click="preview"
        color="warning"
        :disabled="saving"
      >
        Previsualizar
        <span v-show="saving" class="spinner-border spinner-border-sm text-primary" />
      </vs-button>
      <vs-button
        class="mt-5 mr-5"
        data-cy="save"
        @click="save()"
        color="success"
        :disabled="saving"
      >
        Crear
        <span
          v-show="saving"
          class="spinner-border spinner-border-sm text-primary"
        />
      </vs-button>
    </div>
    </form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import ListUser from '@/febos/chile/lists/components/ListUser';
import EditorText from '@/febos/global/customDocuments/vistas/empresas/72235100-2/components/EditorText';
import VInvalidFeedback from '@/febos/global/customDocuments/components/VInvalidFeedback';
import UploadFile from '@/febos/global/_vue/componentes/UploadFile';
import RepositorySelector from '@/febos/global/customDocuments/components/repository-selector/RepositorySelector.vue';
import PdfPreview from '@/febos/global/customDocuments/vistas/empresas/61980520-8/components/PdfPreview.vue';

import { ioCompanyGroups } from '@/febos/servicios/api/empresas.api';
import { ioCreateCustomDocument, ioGetCustomDocument } from '@/febos/servicios/api/customDocuments.api';

export default {
  name: 'DA',
  
  components: {
    ListUser,
    EditorText,
    VInvalidFeedback,
    UploadFile,
    RepositorySelector,
    PdfPreview
  },
  
  data() {
    return {
      formData: {
        // Metadata
        orientation: 'Portrait',
        
        // Identificación
        // numeroDecreto: NO es campo editable - se muestra como texto estático "DECRETO ALCALDICIO N°________"
        // fecha: NO es campo seleccionable - se genera automáticamente en PDF
        comuna: 'Machalí',
        
        // Cuerpo del Decreto
        vistos: '',
        considerando: '',
        decreto: '',
        
        // Visadores
        visadores: [],
        visadoresInitials: '',
        
        // Distribución Interna
        directions: [],
        units: [],
        distribucionInterna: [],
        
        // Distribución Externa
        distribucionExterna: '',
        
        // Adjuntos
        attachments: []
      },
      
      // Opciones para selects
      directions: [],
      units: [],
      
      // Checkboxes para "Seleccionar Todos"
      selectAllDirections: false,
      selectAllUnits: false,
      
      // Estados de carga
      loading: false,
      loadingDirecciones: false,
      loadingDepartamentos: false,
      
      // Preview states
      inPreview: false,
      generatingPreview: false,
      pdfUrl: '',
      saving: false,
      
      // Adjuntos
      fileOthers: {},
      
      // Visadores tracking
      selectedVisators: [],
      visatorsError: '',
      
      // Errores
      error: {
        messageOthers: ''
      }
    };
  },
  
  computed: {
    ...mapGetters('Usuario', ['currentUser']),
    ...mapGetters('Empresas', ['empresa']),
    ...mapGetters('List', ['groupsState']),
    
    isDepartamentosDisabled() {
      return !this.formData.directions || this.formData.directions.length === 0;
    },
    
    isFormValid() {
      return (
        this.formData.vistos &&
        this.formData.considerando &&
        this.formData.decreto &&
        this.formData.visadores.length > 0
      );
    }
  },
  
  watch: {
    'formData.directions': {
      handler(newDirections) {
        if (newDirections && newDirections.length > 0) {
          this.searchUnits(newDirections);
        } else {
          this.units = [];
          this.formData.units = [];
        }
      },
      deep: true
    }
  },
  
  methods: {
    ...mapActions('List', ['fetchGroups']),
    
    back() {
      /* eslint-disable no-restricted-globals */
      history.back();
    },
    
    setInitials(users) {
      if (!users || !users.length) {
        this.formData.visatorsInitials = '';
        this.formData.visadores = [];
        this.selectedVisators = [];
        return;
      }
      
      this.selectedVisators = users;
      this.formData.visadores = users;
      this.visatorsError = '';
      
      const visadoresInitials = users
        .map((u) => u.label.split(' ').map((word) => word.charAt(0).toUpperCase()).join(''));
      
      if (this.currentUser && this.currentUser.nombre) {
        const redactorInitials = this.currentUser.nombre
          .split(' ')
          .map((word) => word.charAt(0).toLowerCase())
          .join('');
        visadoresInitials.push(redactorInitials);
      }
      
      this.formData.visadoresInitials = visadoresInitials.join('/ ');
    },
    
    formatDateForDocument(date) {
      if (!date) return '';
      
      const meses = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ];
      
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      const dia = dateObj.getDate();
      const mes = meses[dateObj.getMonth()];
      const anio = dateObj.getFullYear();
      
      return `MACHALÍ, ${dia} de ${mes} de ${anio}`;
    },
    
    handleVisadoresChange(users) {
      this.setInitials(users);
    },
    
    async loadDirecciones() {
      try {
        this.loadingDirecciones = true;
        
        await this.fetchGroups();
        
        this.directions = this.groupsState.list.filter(
          (group) => group.padreId === '' || !group.padreId
        );
          
      } catch (error) {
        console.error('Error loading direcciones:', error);
        this.$bvToast.toast('Error al cargar direcciones', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
        this.directions = [];
      } finally {
        this.loadingDirecciones = false;
      }
    },
    
    async searchUnits(directions) {
      try {
        this.loadingDepartamentos = true;
        
        const directionsId = directions.map((dir) => dir.id).join(',');
        const { data } = await ioCompanyGroups({
          empresaId: this.empresa.id,
          filtros: `padreId:${directionsId}`
        });
        this.units = data.grupos.map((group) => ({
          ...group,
          id: group.id,
          label: group.nombre,
          value: group.id,
        }));
      } catch (error) {
        console.error('Error loading units:', error);
        this.$bvToast.toast('Error al cargar unidades', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
        this.units = [];
      } finally {
        this.loadingDepartamentos = false;
      }
    },
    
    selectDirections(selectAll) {
      if (selectAll) {
        this.formData.directions = [...this.directions];
        this.searchUnits(this.directions);
      } else {
        this.formData.directions = [];
      }
    },
    
    selectUnits(selectAll) {
      if (selectAll) {
        this.formData.units = [...this.units];
      } else {
        this.formData.units = [];
      }
    },
    
    async addFileRepo(file) {
      if (!Object.keys(file).length) {
        return;
      }

      if (file.failed) {
        this.error.messageOthers = 'La carga del archivo falló, inténtalo de nuevo';
        return;
      }

      this.formData.attachments.push(file);
      this.error.messageOthers = '';
    },
    
    async addOthersFile() {
      if (!Object.keys(this.fileOthers).length) {
        return;
      }

      if (this.fileOthers.failed) {
        this.error.messageOthers = 'La carga del archivo falló, inténtalo de nuevo';
        return;
      }

      this.formData.attachments.push(this.fileOthers);
      this.error.messageOthers = '';
    },
    
    removeOthersFile(fileId) {
      this.formData.attachments = this.formData.attachments
        .filter((file) => file.id !== fileId);
    },
    
    async validateForm(scope) {
      const result = await this.$validator.validateAll(scope);
      return !!result;
    },
    
    stripHtmlTags(html) {
      if (!html) return '';
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    
    cleanEmptyParagraphs(html) {
      if (!html) return '';
      return html.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, '');
    },
    
    validateFieldContent(fieldValue, fieldName) {
      const cleaned = this.cleanEmptyParagraphs(fieldValue);
      const textPlain = this.stripHtmlTags(cleaned).trim();
      
      if (!textPlain || textPlain.length === 0) {
        this.$bvToast.toast(`El campo ${fieldName} no puede estar vacío o contener solo espacios`, {
          title: '¡Oops!',
          variant: 'danger',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 4000,
        });
        return false;
      }
      
      if (textPlain.length > 100000) {
        this.$bvToast.toast(`El campo ${fieldName} tiene más caracteres de los permitidos`, {
          title: '¡Oops!',
          variant: 'danger',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 4000,
        });
        return false;
      }
      
      return true;
    },
    
    async preview() {
      const isValid = await this.validateForm('custom-document');
      if (!this.selectedVisators.length) {
        this.visatorsError = 'El campo visadores es obligatorio';
      }
      if (!isValid || !this.selectedVisators.length) {
        return;
      }
      
      // Validar contenido de campos EditorText
      if (!this.validateFieldContent(this.formData.vistos, 'Vistos')) {
        return;
      }
      
      if (!this.validateFieldContent(this.formData.considerando, 'Considerando')) {
        return;
      }
      
      if (!this.validateFieldContent(this.formData.decreto, 'Decreto')) {
        return;
      }
      
      // Distribución Externa es opcional, solo validar si tiene contenido
      if (this.formData.distribucionExterna) {
        const cleanedDistribucion = this.cleanEmptyParagraphs(this.formData.distribucionExterna);
        const textPlainDistribucion = this.stripHtmlTags(cleanedDistribucion).trim();
        
        if (textPlainDistribucion.length > 100000) {
          this.$bvToast.toast('El campo Distribución Externa tiene más caracteres de los permitidos', {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          });
          return;
        }
      }
      
      this.pdfUrl = '';
      this.inPreview = true;
      this.generatingPreview = true;
      
      try {
        this.$bvToast.toast('Generando previsualización', {
          title: 'En camino...',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 3000
        });
        
        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            simular: 'si'
          },
          formData: {
            ...this.formData,
            vistos: this.cleanEmptyParagraphs(this.formData.vistos),
            considerando: this.cleanEmptyParagraphs(this.formData.considerando),
            decreto: this.cleanEmptyParagraphs(this.formData.decreto),
            distribucionExterna: this.cleanEmptyParagraphs(this.formData.distribucionExterna),
            fecha: this.formatDateForDocument(new Date()),
            metadata: { orientation: this.formData.orientation },
            borrador: true
          }
        };
        
        const { data: { pdf } } = await ioCreateCustomDocument(payload);
        
        if (!pdf || pdf === '') {
          this.$bvToast.toast('No se pudo generar la previsualización', {
            title: 'Ups!',
            variant: 'warning',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 5000
          });
          this.backToForm();
          return;
        }
        
        this.pdfUrl = `${pdf}#toolbar=0&navpanes=0&scrollbar=0`;
        
      } catch (error) {
        console.error('Error generating preview:', error);
        
        const errorMessage = error.response?.data?.message || 'Error al generar la previsualización';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
        
        this.backToForm();
        
      } finally {
        this.generatingPreview = false;
      }
    },
    
    backToForm() {
      this.inPreview = false;
    },
    
    async save() {
      const isValid = await this.validateForm('custom-document');
      if (!this.selectedVisators.length) {
        this.visatorsError = 'El campo visadores es obligatorio';
      }
      if (!isValid || !this.selectedVisators.length) {
        return;
      }
      
      // Validar contenido de campos EditorText
      if (!this.validateFieldContent(this.formData.vistos, 'Vistos')) {
        return;
      }
      
      if (!this.validateFieldContent(this.formData.considerando, 'Considerando')) {
        return;
      }
      
      if (!this.validateFieldContent(this.formData.decreto, 'Decreto')) {
        return;
      }
      
      // Distribución Externa es opcional, solo validar si tiene contenido
      if (this.formData.distribucionExterna) {
        const cleanedDistribucion = this.cleanEmptyParagraphs(this.formData.distribucionExterna);
        const textPlainDistribucion = this.stripHtmlTags(cleanedDistribucion).trim();
        
        if (textPlainDistribucion.length > 100000) {
          this.$bvToast.toast('El campo Distribución Externa tiene más caracteres de los permitidos', {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          });
          return;
        }
      }
      
      this.$bvToast.toast('Guardando documento', {
        title: 'En camino...',
        variant: 'primary',
        pauseOnHover: true,
        solid: true,
        autoHideDelay: 2000
      });
      
      try {
        this.saving = true;
        
        const payload = {
          type: this.$route.params.type,
          query: this.$route.query,
          formData: {
            ...this.formData,
            vistos: this.cleanEmptyParagraphs(this.formData.vistos),
            considerando: this.cleanEmptyParagraphs(this.formData.considerando),
            decreto: this.cleanEmptyParagraphs(this.formData.decreto),
            distribucionExterna: this.cleanEmptyParagraphs(this.formData.distribucionExterna),
            fecha: this.formatDateForDocument(new Date()),
            metadata: { orientation: this.formData.orientation }
          }
        };
        
        const response = await ioCreateCustomDocument(payload);
        
        await this.generatePdf(response.data.document.id);
        
      } catch (error) {
        console.error('Error saving document:', error);
        
        this.$bvToast.toast('Error al guardar el documento, inténtalo de nuevo', {
          title: '¡Oops!',
          variant: 'danger',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 4000
        });
        
      } finally {
        this.saving = false;
      }
    },
    
    async generatePdf(id) {
      this.$bvToast.toast(
        'Generando archivo PDF',
        {
          title: 'En camino...',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 5000
        }
      );
      try {
        const response = await ioGetCustomDocument('DA', id, { pdf: 'si' });
        this.$bvToast.toast(
          'Archivo generado',
          {
            title: 'Listo!',
            variant: 'success',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 2000
          }
        );
        window.open(response.data.pdf, '_blank');
        this.back();
      } catch (error) {
        this.$bvToast.toast(
          'Error al generar el PDF, inténtalo de nuevo',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
      }
    }
  },
  
  created() {
    this.loadDirecciones();
  },
  
  mounted() {
    this.$validator.localize('es', {
      custom: {
        vistos: {
          required: 'El campo Vistos es requerido'
        },
        considerando: {
          required: 'El campo Considerando es requerido'
        },
        decreto: {
          required: 'El campo Decreto es requerido'
        },
        visadores: {
          required: 'Debe seleccionar al menos un visador'
        }
      }
    });
  }
};
</script>
